import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { planApi, enterpriseApi, PaymentRecord } from '@/api/planApi';
import { Spinner } from '@/components/ui/spinner';
import { parseServerTime } from '@/lib/serverTime';

interface RefundConfirmDialogProps {
  /** 환불 대상 결제 건. null이면 다이얼로그가 닫혀 있는 상태 */
  payment: PaymentRecord | null;
  /** 취소/닫기 시 호출 (부모가 payment를 null로 되돌려야 함) */
  onClose: () => void;
  /** 팀 컨텍스트 여부 (팀 비활성화 경고 문구 표시용) */
  isTeamContext?: boolean;
  /** 환불 성공 시 호출 (부모가 알림 표시 + 목록 새로고침을 처리) */
  onRefunded: (result: { message: string; teamStatus?: string | null }) => void | Promise<void>;
}

/**
 * 환불 확인 다이얼로그 (BillingPage/PlanPage 공용).
 * 계좌이체는 환불 계좌 정보를 입력받고, 엔터프라이즈(개인/팀)는 전용 환불 API로,
 * 일반 플랜(개인/팀)은 일반 환불 API로 분기한다. 과거 두 페이지가 이 로직을 각자
 * 구현하면서 PlanPage 쪽이 계좌 정보 입력과 엔터프라이즈 분기 없이 방치돼
 * 실제로는 절대 성공할 수 없거나(계좌이체) 잘못된 API로 환불되던(엔터프라이즈) 문제가
 * 있었음 - 한 곳으로 합쳐 다시 어긋나지 않게 한다.
 */
export function RefundConfirmDialog({ payment, onClose, isTeamContext, onRefunded }: RefundConfirmDialogProps) {
  const [refunding, setRefunding] = useState(false);
  const [refundBankInfo, setRefundBankInfo] = useState({ bankName: '', accountNumber: '', accountHolder: '' });
  const [refundError, setRefundError] = useState<string | null>(null);

  useEffect(() => {
    if (payment) {
      setRefundBankInfo({ bankName: '', accountNumber: '', accountHolder: '' });
      setRefundError(null);
    }
  }, [payment]);

  const handleConfirmRefund = async () => {
    if (!payment) return;
    setRefundError(null);

    const isBankTransfer = payment.paymentMethod === 'BANK_TRANSFER';
    if (isBankTransfer) {
      if (!refundBankInfo.bankName.trim()) {
        setRefundError('은행명을 입력해주세요.');
        return;
      }
      if (!refundBankInfo.accountNumber.trim()) {
        setRefundError('계좌번호를 입력해주세요.');
        return;
      }
      if (!refundBankInfo.accountHolder.trim()) {
        setRefundError('예금주명을 입력해주세요.');
        return;
      }
    }

    setRefunding(true);
    try {
      const currentPlan = payment.plan;
      const contractId = payment.contractId;
      const teamId = payment.teamId;
      const refundData = {
        cancelReason: '사용자 요청',
        ...(isBankTransfer
          ? {
              refundBankName: refundBankInfo.bankName.trim(),
              refundAccountNumber: refundBankInfo.accountNumber.trim(),
              refundAccountHolder: refundBankInfo.accountHolder.trim(),
            }
          : {}),
      };

      let res;
      if (currentPlan === 'ENTERPRISE' && contractId && teamId) {
        // 팀 엔터프라이즈 환불 API 호출 (팀장만 가능 - 팀장이 아니면 서버에서 에러 반환)
        res = await enterpriseApi.requestTeamRefund(teamId, contractId, refundData);
      } else if (currentPlan === 'ENTERPRISE' && contractId) {
        // 개인 엔터프라이즈 환불 API 호출
        res = await enterpriseApi.requestRefund(contractId, refundData);
      } else {
        // 일반 플랜 환불 API 호출 (개인/팀 공통)
        res = await planApi.refundPayment(payment.id, refundData);
      }

      const fallbackMessage = isBankTransfer
        ? `환불이 접수되었습니다.\n환불 금액: ${res.data.refundAmount.toLocaleString()}원`
        : `환불이 완료되었습니다.\n환불 금액: ${res.data.refundAmount.toLocaleString()}원`;
      const message = res.data.message || fallbackMessage;
      await onRefunded({ message, teamStatus: res.data.teamStatus });
    } catch (err: any) {
      setRefundError(err?.response?.data?.message ?? '환불 중 오류가 발생했습니다.');
    } finally {
      setRefunding(false);
    }
  };

  return (
    <Dialog open={!!payment} onOpenChange={(open) => !refunding && !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>환불 확인</DialogTitle>
        </DialogHeader>
        {payment && (
          <div className="space-y-4">
            <DialogDescription>
              결제일로부터 7일 이내인 경우에만 환불이 가능합니다.
            </DialogDescription>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">결제 금액</span>
                <span className="font-semibold">{payment.amount.toLocaleString()}원</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">결제일</span>
                <span className="font-semibold">{parseServerTime(payment.createdAt)?.toLocaleDateString('ko-KR')}</span>
              </div>
            </div>
            {payment.paymentMethod === 'BANK_TRANSFER' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">환불받을 계좌 정보를 입력해주세요.</p>
                <div className="space-y-2">
                  <Label htmlFor="refundBankName">은행명</Label>
                  <Input
                    id="refundBankName"
                    placeholder="예: 국민은행"
                    value={refundBankInfo.bankName}
                    onChange={(e) => setRefundBankInfo((prev) => ({ ...prev, bankName: e.target.value }))}
                    disabled={refunding}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refundAccountNumber">계좌번호</Label>
                  <Input
                    id="refundAccountNumber"
                    placeholder="'-' 없이 숫자만 입력"
                    value={refundBankInfo.accountNumber}
                    onChange={(e) => setRefundBankInfo((prev) => ({ ...prev, accountNumber: e.target.value }))}
                    disabled={refunding}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refundAccountHolder">예금주명</Label>
                  <Input
                    id="refundAccountHolder"
                    placeholder="예: 홍길동"
                    value={refundBankInfo.accountHolder}
                    onChange={(e) => setRefundBankInfo((prev) => ({ ...prev, accountHolder: e.target.value }))}
                    disabled={refunding}
                  />
                </div>
              </div>
            )}
            {refundError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription className="text-xs">{refundError}</AlertDescription>
              </Alert>
            )}
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-700 text-xs">
                {payment.teamId
                  ? `환불 시 ${payment.teamName ? `'${payment.teamName}' ` : ''}팀이 비활성화되며, 팀원들은 더 이상 서비스를 이용할 수 없습니다. (팀장만 환불 가능)`
                  : '환불 시 플랜이 FREE로 변경됩니다.'}
                {!payment.teamId && isTeamContext && ' 팀이 비활성화됩니다. 모든 팀원이 서비스를 이용할 수 없게 됩니다.'}
              </AlertDescription>
            </Alert>
            <div className="flex gap-3 justify-end mt-4">
              <Button variant="outline" onClick={onClose} disabled={refunding}>
                취소
              </Button>
              <Button onClick={handleConfirmRefund} disabled={refunding}>
                {refunding ? <Spinner size="sm" className="mr-2" /> : null}
                환불
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
